import React from 'react';

/**
 * Generic "stepper" row: a label on the left and - / count / + controls on the
 * right. Intentionally free of book-event specifics so the same component can be
 * reused by book-appointment and buy-membership later (RNA-4900 and friends):
 * the caller supplies the label, the current count, the DOM ids to preserve and
 * the increment/decrement callbacks.
 *
 * Rendering mirrors the legacy Angular markup exactly (same wrapper structure and
 * class names: list-text-left / list-text-right / less / number / more) so the
 * existing modal-bookbuy SCSS styles it without any visual change.
 */
export type GuestCounterData = {
  label?: string;
  guests?: number;
  /**
   * Plumbed through for future use; per RNA-4896 it deliberately has no visual
   * effect yet, and the decrement still fires (the Angular controller already
   * guards against going below zero), keeping behaviour identical to today.
   */
  decreaseDisabled?: boolean;
  countId?: string;
  increaseId?: string;
  decreaseId?: string;
  onIncrease?: () => void;
  onDecrease?: () => void;
};

type Props = {
  data: GuestCounterData;
};

export function BookEventGuestCounter({ data }: Props) {
  if (!data) {
    return null;
  }

  const {
    label = '',
    guests = 0,
    countId,
    increaseId,
    decreaseId,
    onIncrease,
    onDecrease,
  } = data;

  const handleDecrease = () => {
    if (onDecrease) {
      onDecrease();
    }
  };

  const handleIncrease = () => {
    if (onIncrease) {
      onIncrease();
    }
  };

  return (
    <>
      <div className="list-text-left">
        <p>{label}</p>
      </div>
      <div className="list-text-right">
        <p>
          <span id={decreaseId || undefined} className="less" onClick={handleDecrease}>
            -
          </span>
          <span id={countId || undefined} className="number">
            {guests}
          </span>
          <span id={increaseId || undefined} className="more" onClick={handleIncrease}>
            +
          </span>
        </p>
      </div>
    </>
  );
}
